import { Component, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

// DOM errors caused by browser extensions (Google Translate, Grammarly, etc.)
// modifying nodes between React's reconciliation phases. Not recoverable via
// state reset — React's internal fiber tree is corrupted. Auto-retry once.
const isDomManipulationError = (error: Error): boolean =>
  error.name === 'NotFoundError' &&
  error.message.includes('removeChild');

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Browser extension DOM manipulation — auto-recover once
    if (isDomManipulationError(error) && this.state.retryCount < 1) {
      console.warn('[ErrorBoundary] DOM manipulation error (likely browser extension) — auto-recovering');
      this.setState({ hasError: false, error: undefined, retryCount: this.state.retryCount + 1 });
      return;
    }

    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Capture error to Sentry with error boundary context
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        errorBoundary: 'true',
        isDomExtensionError: String(isDomManipulationError(error)),
      },
      level: isDomManipulationError(error) ? 'warning' : 'error',
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <div className="flex items-center gap-2 text-destructive mb-2">
                <AlertTriangle className="h-5 w-5" />
                <CardTitle>Une erreur s'est produite</CardTitle>
              </div>
              <CardDescription>
                Nous sommes désolés, mais quelque chose s'est mal passé.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {this.state.error && (
                <div className="bg-muted p-3 rounded-md">
                  <p className="text-sm font-mono text-muted-foreground">
                    {this.state.error.message}
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={this.handleReset} variant="default">
                  Réessayer
                </Button>
                <Button onClick={() => window.location.href = '/'} variant="outline">
                  Retour à l'accueil
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
